import React, { useState, useEffect, useRef } from 'react'
import addIcon from '../../assets/images/add.png'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import { createRepository, getImageTags } from '../../redux/slices/apiSlice'
import UseToast from '../../hooks/useToast'
import useAuth from '../../hooks/useAuth'
import { FILE_TYPES, FILE_SIZE } from '../../utils'
import { useSelector } from 'react-redux'
import Loader from '../../utils/Loader'
import Chip from '../../utils/Chip'
import { RxCaretDown, RxCaretUp } from 'react-icons/rx'
import Select from "react-select";
import moment from "moment";
import TagInput from "@pathofdev/react-tag-input";
import "@pathofdev/react-tag-input/build/index.css";


const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const UploadFileContent = ({
  onClose,
  currentParentId,
  fetchFolders,
  openUploadBtnDialog,
  folderList
}) => {
  const { createRepoLoading } = useSelector((state) => state.api) // Updated selector
  const formikRef = useRef(null)
  const [showMonths, setShowMonths] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialValues, setInitialValues] = useState({
    uploadedFile: null,
    name: '',
    description: '',
    tags: '',
    imageTags: "",
    yearlyTags: { label: moment().year(), value: moment().year() },
  })
  const { isLoggedin, user } = useAuth()
  const fileInputRef = useRef(null);

  const dispatch = useDispatch()
  const { successToast, errorToast } = UseToast()

  const handleChipClick = (month, setFieldValue) => {
    setSelectedMonth(month)
    setFieldValue('tags', [month]) // Ensure only one month is selected
  }

  const handleChipRemove = (month, setFieldValue) => {
    if (selectedMonth === month) {
      setSelectedMonth(null)
      setFieldValue('tags', [])
    }
  }

  const getImageTagHandler = async (fileObj, setFieldValue) => {
    try {
      // setTimeout(() => {
      if (fileObj) {
        const fileExtension = fileObj.name.split('.').pop().toLowerCase();
        if (fileExtension === 'psd' && !fileObj.type) {
          const newFile = new File([fileObj], fileObj.name, { type: 'application/x-photoshop' });
          setFieldValue("uploadedFile", newFile);
        } else {
          setFieldValue("uploadedFile", fileObj);
        }
      }
      // }, 0);
      setLoading(true);
      const formData = new FormData()
      formData.append('upload_file', fileObj);
      let response = await dispatch(
        getImageTags(formData)
      )
      formikRef.current?.setFieldValue("imageTags", response?.payload?.data?.join(","));
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  }

  const validationSchema = Yup.object().shape({
    // uploadedFile: Yup.mixed().required("File is required"),
    uploadedFile: Yup.mixed()
      .test(
        'fileType',
        'Invalid file type. Please upload a supported file format.',
        (file) => {
          return file && [...FILE_TYPES.DOC_TYPES, ...FILE_TYPES.IMAGE_TYPES, ...FILE_TYPES.VIDEO_TYPES].includes(file.type)
        }
      )
      .test("fileSize", function (file) {
        if (!file) return false;
        const fileTypeCategory = file.type.startsWith("image/")
          ? "IMAGE_MAX_SIZE"
          : file.type.startsWith("video/")
            ? "VIDEO_MAX_SIZE"
            : "DOC_MAX_SIZE";
        const maxSize = FILE_SIZE[fileTypeCategory];
        const maxSizeMB = maxSize / (1024 * 1024);
        return file.size <= maxSize
          ? true
          : this.createError({
            message: `File "${file.name}" exceeds the allowed limit of ${maxSizeMB} MB.`,
          });
      })
      .required('File is required'),
    name: Yup.string()
      .min(3, "File Name must be at least 3 characters")
      .max(255, "File Name cannot exceed 255 characters")
      .matches(/^[a-zA-Z0-9 _-]+$/, "Only alphanumeric characters, spaces, hyphens (-), and underscores (_) are allowed")
      .required("File Name is required"),
    description: Yup.string().nullable(),
    imageTags: Yup.string().nullable(),
    tags: Yup.array().min(1, 'Please select one Tag').required('Tag is required'),
    yearlyTags: Yup.object().required("Yearly tag is required"),
  })

  useEffect(() => {
    setInitialValues((prev) => ({
      ...prev,
      uploadedFile: null,
      name: '',
      description: '',
      tags: [],
      imageTags: "",
      yearlyTags: { label: moment().year(), value: moment().year() }
    }))
    setSelectedMonth([])
    setShowMonths(false)
    if (formikRef.current) {
      formikRef.current.resetForm()
    }
  }, [openUploadBtnDialog])

  const token = sessionStorage.getItem('token')

  return (
    <>
      {createRepoLoading || loading ? <Loader /> : <></>}
      <Formik
        innerRef={formikRef}
        key={JSON.stringify(initialValues)}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (values, { resetForm }) => {
          try {
            const headers = {
              accept: 'application/json',
              authorization: token,
            }
            let folderUrl = "";
            // if (Array.isArray(folderList?.result) && !folderList?.result[0]?.parent_id) {
            //   folderUrl = `${folderList?.result?.find(item => item._id === currentParentId)?.name || ""}/${values.name}`
            // } else {
            //   folderUrl = `${folderList?.result?.find(item => item._id === currentParentId)?.folderUrl || ""}/${values.name}`
            // }
            const formData = new FormData()
            formData.append('uploadedFile', values.uploadedFile)
            formData.append('name', values.name)
            formData.append('yearlyTags', values.yearlyTags?.value || moment().year())

            formData.append('description', values.description)
            formData.append('imageTags', values.imageTags)
            formData.append('tags', values.tags)
            formData.append('type', 'file')
            formData.append('owner_id', user?._id)
            formData.append('parent_id', currentParentId)
            formData.append('folderUrl', folderUrl)
            let response = await dispatch(
              createRepository({ payload: formData, headers })
            )
            if (response?.payload?.message) {
              successToast('File uploaded successfully.')
              onClose()
              fetchFolders(currentParentId)
              resetForm()
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            } else if (response?.error?.message) {
              errorToast(response?.error?.message)
            }
          } catch (error) {
            console.error('Upload error:', error)
          }
        }}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div className='formBox uploadPopup'>
              <div className='form-group'>
                <label className='file' htmlFor='uploadFile1'>
                  <span>
                    <img src={addIcon} alt='' />
                  </span>
                  Add File
                </label>
                <input
                  type='file'
                  id='uploadFile1'
                  ref={fileInputRef}
                  accept='image/*, video/*, application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .psd'
                  onChange={(event) => {
                    // setFieldValue('uploadedFile', event.currentTarget.files[0]);
                    getImageTagHandler(event.currentTarget.files[0], setFieldValue);
                  }}
                />
                {values.uploadedFile && (
                  <div className='selected-file'>
                    {values.uploadedFile.name} - (
                    {`${values.uploadedFile.size
                      ? (values.uploadedFile.size / (1024 * 1024)).toFixed(2)
                      : 0
                      } MB`}
                    )
                  </div>
                )}
                <ErrorMessage
                  name='uploadedFile'
                  component='div'
                  className='error-message'
                />
              </div>

              <div className='form-group'>
                <label>File Name</label>
                <Field
                  className='form-control'
                  type='text'
                  name='name'
                  maxLength={255}
                  placeholder='File Name'
                />
                <ErrorMessage
                  name='name'
                  component='div'
                  className='error-message'
                />
              </div>

              <div className='form-group'>
                <label>Description</label>
                <Field
                  as='textarea'
                  className='form-control'
                  name='description'
                  placeholder='Description'
                  spellCheck='false'
                />
                <ErrorMessage
                  name='description'
                  component='div'
                  className='error-message'
                />
              </div>

              {/* <div className='form-group'>
                <label>Tags</label>
                <Field
                  className='form-control'
                  type='text'
                  name='tags'
                  placeholder='Tags'
                  value={selectedMonths.join(',')}
                  readOnly
                />

                <button
                  type='button'
                  className='!mt-2 bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow'
                  onClick={() => setShowMonths(!showMonths)}
                >
                  Month
                </button>
                {showMonths && (
                  <div className='flex gap-2 flex-wrap mt-2 cursor-pointer'>
                    {months.map((month, index) => (
                      <Chip
                        key={index}
                        label={month}
                        onClick={() => handleChipClick(month, setFieldValue)}
                      />
                    ))}
                  </div>
                )}

                <ErrorMessage
                  name='tags'
                  component='div'
                  className='error-message'
                />
              </div> */}


              <div className='form-group'>
                <label>Tags</label>
                {/* <Field
                  className='form-control'
                  type='text'
                  name='imageTags'
                  maxLength={255}
                  placeholder='Image Tags'
                /> */}
                <TagInput
                  tags={values.imageTags ? values.imageTags.split(',').map(t => t.trim()).filter(Boolean) : []}
                  onChange={(newTags) => {
                    setFieldValue('imageTags', newTags.join(','));
                  }}
                  placeholder="Enter Tags"
                />
                <ErrorMessage
                  name='imageTags'
                  component='div'
                  className='error-message'
                />
              </div>

              <div className='form-group'>
                <label>Yearly Tags</label>
                <Select
                  options={[
                    { label: "2024", value: "2024" },
                    { label: "2025", value: "2025" },
                    { label: "2026", value: "2026" },
                  ]}
                  className='customSelectBox'
                  placeholder="Yearly Tags"
                  onChange={(option) =>
                    setFieldValue("yearlyTags", option)
                  }
                  value={values.yearlyTags}
                />
                <ErrorMessage
                  name='yearlyTags'
                  component='div'
                  className='error-message'
                />
              </div>


              <div className='form-group'>
                <label>Monthly Tags</label>
                <div className='min-h-[40px] max-h-[100px] overflow-y-auto border border-gray-300 rounded-md p-2 flex flex-wrap gap-2'>
                  {selectedMonth && selectedMonth.length > 0 ? (
                    <Chip
                      key={selectedMonth}
                      label={selectedMonth}
                      onClick={() => console.log('static chip')}
                      onRemove={() =>
                        handleChipRemove(selectedMonth, setFieldValue)
                      }
                    />
                  ) : (
                    <span className='text-gray-400'>Select a tag...</span>
                  )}
                </div>
                <ErrorMessage
                  name='tags'
                  component='div'
                  className='error-message'
                />
                <div className='relative'>
                  <button
                    type='button'
                    className='!mt-2 bg-white flex gap-2 items-center hover:bg-blue-50 text-gray-600 font-semibold text-xs py-1 px-2 border border-gray-300 rounded-full '
                    onClick={() => setShowMonths(!showMonths)}
                  >
                    <span>Months</span>
                    <span className='text-blue-400 text-base'>
                      {showMonths ? <RxCaretUp /> : <RxCaretDown />}
                    </span>
                  </button>

                  <div
                    className={`flex flex-wrap gap-2 mt-1 cursor-pointer bg-white  rounded-md py-2 transition-all duration-700 ease-in-out overflow-hidden transform origin-top ${showMonths
                      ? 'max-h-40 scale-y-100 opacity-100'
                      : 'max-h-0 scale-y-0 opacity-0'
                      }`}
                  >
                    {months.map((month, index) => (
                      <Chip
                        key={index}
                        label={month}
                        onClick={() => handleChipClick(month, setFieldValue)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className='form-group !mb-0'>
                <button className='blueBtn' type='submit'>
                  Upload
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default UploadFileContent
